import { useNavigate } from "react-router-dom";
import {
  useVenueDetailsQuery,
  useVenueUpdateMutation,
} from "@ticketingplatform/api/dist/venue";
import { message } from "antd";
import { useIntl } from "react-intl";
import usePrompt from "hooks/usePrompt";
import { ROUTE_PATHS } from "const/paths";
import { useCallback } from "react";
import { useNumberParams } from "hooks/useNumberParams";
import useApolloErrorFields from "hooks/useApolloErrorFields";
import getApolloNonFieldErrors from "utils/getApolloNonFieldErrors";
import { ErrorCode } from "@ticketingplatform/api/dist/const/enums";
import useImageUpload from "hooks/useImageUpload";

import { useVenueShape } from "../hooks/useVenueShape";
import { VenueFormProps, VenueFormValues } from "../VenueForm/types";

export const useVenueEditForm = (): VenueFormProps => {
  const navigate = useNavigate();
  const { id } = useNumberParams(true);
  const intl = useIntl();
  const { data, loading } = useVenueDetailsQuery({ variables: { id } });
  const { venue } = data || {};
  const [updateVenue, { loading: submitting, error }] =
    useVenueUpdateMutation();
  const fields = useApolloErrorFields(error);
  const { uploadImage, isUploadLoading } = useImageUpload();

  usePrompt(submitting);

  const onFinish = useCallback(
    async ({
      input: {
        organizationId: _,
        image,
        address: { country, ...address },
        ...input
      },
    }: VenueFormValues) => {
      try {
        const imageId = await (async () => {
          if (!image) {
            return data?.venue?.image?.id ? null : undefined;
          }

          if ("id" in image) {
            return undefined;
          }

          const {
            image: { id: internalImageId },
          } = await uploadImage(image);

          return internalImageId;
        })();

        await updateVenue({
          variables: {
            id,
            input: {
              ...input,
              imageId,
              address: {
                ...address,
                countryCode: country?.value,
                country: country?.label,
              },
            },
          },
        });

        message.success(
          intl.formatMessage(
            { defaultMessage: 'Venue "{name}" has been updated!' },
            { name: input.name }
          )
        );

        navigate(ROUTE_PATHS.VenuesList, {
          state: {
            skipBlock: true,
          },
        });
      } catch (err) {
        const nonFieldError = getApolloNonFieldErrors(err).find(
          ({ errorType }) => errorType && errorType !== ErrorCode.INTERNAL_ERROR
        )?.message;

        message.error(
          nonFieldError ||
            intl.formatMessage(
              { defaultMessage: 'Failed to update Venue "{name}".' },
              { name: input.name }
            )
        );
      }
    },
    [id, intl, navigate, updateVenue, uploadImage, data?.venue?.image?.id]
  );

  return {
    fields,
    loading: loading || submitting || isUploadLoading,
    initialValues: useVenueShape(venue),
    onFinish,
  };
};
